/**
 * @file TemplateVisualEditor.tsx
 * @description Визуальный (Word-like) редактор шаблонов документов
 * (phase19.7): TipTap + таблицы + выравнивание + отступы + чипы-плейсхолдеры.
 *
 * value/onChange — Handlebars-HTML (то, что хранится в htmlSource шаблона);
 * трансформы «шаблон ⇄ редактор» — lib/template-editor-transforms.ts.
 * Компонент монтируется с готовым value и НЕ ресинхронизируется извне —
 * при переключении режима «Визуально ⇄ HTML» родитель пересоздаёт его по key.
 */

import { Table, TableCell, TableHeader } from "@tiptap/extension-table";
import { TextAlign } from "@tiptap/extension-text-align";
import { EditorContent, useEditor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";

import {
  Indent,
  ItemsTableRow,
  PlaceholderChip,
} from "../../lib/template-editor-extensions";
import {
  editorHtmlToHandlebars,
  handlebarsToEditorHtml,
} from "../../lib/template-editor-transforms";
import { TemplateEditorToolbar } from "./TemplateEditorToolbar";

interface Props {
  /** Handlebars-HTML шаблона. */
  value: string;
  onChange: (handlebarsHtml: string) => void;
  disabled?: boolean;
}

export function TemplateVisualEditor({ value, onChange, disabled }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Table.configure({ resizable: false }),
      ItemsTableRow,
      TableCell,
      TableHeader,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Indent,
      PlaceholderChip,
    ],
    content: handlebarsToEditorHtml(value || "<p></p>"),
    editable: !disabled,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class:
          "tpl-editor prose prose-sm max-w-none min-h-[320px] focus:outline-none px-4 py-3 bg-white text-neutral-900",
      },
    },
    onUpdate({ editor: e }) {
      onChange(editorHtmlToHandlebars(e.getHTML()));
    },
  });

  if (!editor) return null;

  return (
    <div className="rounded-md border border-outline-variant/50 overflow-hidden">
      <TemplateEditorToolbar editor={editor} />
      <div className="max-h-[52vh] overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
